import type {
  ActivityItem,
  ApiErrorBody,
  ApiErrorCode,
  CalendarEvent,
  CalendarInfo,
  ChatMessage,
  ConnectedAccount,
  ConversationSummary,
  CreateChannelInput,
  CreateMeetingInput,
  DeviceSession,
  Draft,
  DraftUpsertInput,
  EventInput,
  EventUpdateInput,
  FileRef,
  Invitation,
  JoinContext,
  MailDetail,
  MailFolder,
  MailPage,
  MailSummary,
  MeetingParticipant,
  MeetingSummary,
  MessagePage,
  OccurrenceSummary,
  Operation,
  ParticipantRole,
  PersonRef,
  PostMessageInput,
  SearchResults,
  SessionInfo,
  ShareMailToChatInput,
  SpacemailConnectInput,
  ImapConnectInput,
  MailDetection,
  UpdateSettingsInput,
  WorkspaceMember,
  ConnectionProblem,
  ServiceState,
  CalendarCapabilities,
  RemoteAuthority,
  RemoteEnrollment,
  RemoteHost,
  RemoteHostAccess,
  RemoteLinkPreview,
  RemotePermission,
  RemoteSession,
  RemoteSupportCode,
  RemoteTicket,
  RemoteUserKey,
  SignedEnvelope,
  WorkspaceDomain,
} from '@deskworks/domain';

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: ApiErrorCode | 'network' | 'offline',
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
  /** True when the request may not have reached the server (safe to retry with the same id). */
  get transient() {
    return this.code === 'network' || this.code === 'offline' || this.status === 503 || this.status === 502 || this.status === 504;
  }
}

export interface ApiClientOptions {
  /** Base URL of the API ('' for same-origin browsers). */
  baseUrl: string;
  /** 'cookie' for browsers (HttpOnly session + CSRF token), 'bearer' for native apps. */
  mode: 'cookie' | 'bearer';
  getToken?: () => Promise<string | null> | string | null;
  fetchImpl?: typeof fetch;
  onUnauthenticated?: () => void;
}

export interface OccurrenceDetail {
  occurrence: OccurrenceSummary;
  meeting: JoinContext['meeting'];
  me: MeetingParticipant | null;
  isHost: boolean;
  hostPresent: boolean;
  participants: MeetingParticipant[];
}

export interface ListedOccurrence extends OccurrenceSummary {
  myRole: ParticipantRole;
  organizerName: string;
}

export interface PersonCard {
  id: string;
  displayName: string;
  initials: string;
  email: string;
  timeZone: string;
  isMe: boolean;
  workspaces: { id: string; name: string; role: string }[];
  directConversationId: string | null;
  sharedConversations: { id: string; name: string; kind: string }[];
  recentMail: MailSummary[];
}

export interface Person extends PersonRef {
  email: string;
  isMe?: boolean;
}

export interface ListedFile extends FileRef {
  conversationName: string | null;
  uploaderName: string;
}

/** Result of connecting a mailbox with a password (Spacemail or IMAP). */
export interface SpacemailConnectResponse {
  accountId: string | null;
  email: string;
  services: { mail: ServiceState; calendar: ServiceState; contacts: ServiceState };
  problem: ConnectionProblem | null;
  /** Provider-specific advice for `problem` (IMAP presets), shown instead of the generic copy. */
  detail?: string | null;
  calendars: { remoteId: string; name: string }[];
  capabilities: CalendarCapabilities;
  account?: ConnectedAccount | null;
}

export type MailboxConnectResponse = SpacemailConnectResponse;

export interface MailQuery {
  view?: 'inbox' | 'starred' | 'sent' | 'drafts' | 'archive' | 'trash' | 'junk' | 'folder';
  folderId?: string;
  accountId?: string;
  focus?: 'focused' | 'other' | 'all';
  filter?: 'all' | 'unread' | 'starred' | 'attachments';
  cursor?: string;
  limit?: number;
}

function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

/**
 * Typed DeskWorks One API client shared by web, desktop and mobile. Browsers use the HttpOnly
 * session cookie plus a CSRF token; native apps send a bearer token from the OS keychain.
 */
export class ApiClient {
  csrfToken: string | null = null;
  private readonly f: typeof fetch;

  constructor(readonly opts: ApiClientOptions) {
    this.f = opts.fetchImpl ?? ((...a) => fetch(...a));
  }

  get baseUrl() {
    return this.opts.baseUrl;
  }

  async request<T>(method: string, path: string, body?: unknown, init: { signal?: AbortSignal; raw?: boolean } = {}): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' };
    const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
    if (body !== undefined && !isForm) headers['content-type'] = 'application/json';
    if (this.opts.mode === 'cookie' && this.csrfToken && method !== 'GET') headers['x-dw-csrf'] = this.csrfToken;
    if (this.opts.mode === 'bearer') {
      const token = await this.opts.getToken?.();
      if (token) headers.authorization = `Bearer ${token}`;
    }
    let res: Response;
    try {
      res = await this.f(`${this.opts.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
        credentials: this.opts.mode === 'cookie' ? 'include' : 'omit',
        signal: init.signal,
      });
    } catch (err) {
      if ((err as Error).name === 'AbortError') throw err;
      const offline = typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine === false;
      throw new ApiRequestError(0, offline ? 'offline' : 'network', offline ? 'You are offline.' : 'The server could not be reached.');
    }
    if (init.raw) return res as unknown as T;
    const text = await res.text();
    const json = text ? (JSON.parse(text) as unknown) : null;
    if (!res.ok) {
      const e = (json as ApiErrorBody | null)?.error;
      if (res.status === 401 && path !== '/v1/session') this.opts.onUnauthenticated?.();
      throw new ApiRequestError(res.status, e?.code ?? 'internal', e?.message ?? `Request failed (${res.status})`, e?.details);
    }
    if (json && typeof json === 'object' && 'csrfToken' in json && typeof (json as { csrfToken?: unknown }).csrfToken === 'string') {
      this.csrfToken = (json as { csrfToken: string }).csrfToken;
    }
    return json as T;
  }

  get = <T>(path: string, signal?: AbortSignal) => this.request<T>('GET', path, undefined, { signal });
  post = <T>(path: string, body: unknown = {}) => this.request<T>('POST', path, body);
  patch = <T>(path: string, body: unknown = {}) => this.request<T>('PATCH', path, body);
  put = <T>(path: string, body: unknown = {}) => this.request<T>('PUT', path, body);
  del = <T>(path: string, body?: unknown) => this.request<T>('DELETE', path, body);

  /* ------------------------------------------------------------- identity */

  authStatus = () => this.get<{ needsSetup: boolean; setupCodeRequired?: boolean }>('/v1/auth/status');
  setup = (input: { email: string; displayName: string; password: string; workspaceName: string; timeZone: string; setupCode?: string }) =>
    this.post<SessionInfo>('/v1/auth/setup', input);
  signIn = (input: { email: string; password: string; client?: 'web' | 'native'; deviceLabel?: string }) =>
    this.post<SessionInfo & { token?: string }>('/v1/auth/sign-in', input);
  signOut = () => this.post<{ ok: true }>('/v1/auth/sign-out');
  session = () => this.get<SessionInfo>('/v1/session');
  invitationPreview = (token: string) =>
    this.get<{ workspaceName: string; email: string; invitedBy: string; expired: boolean; accepted: boolean; accountExists: boolean }>(
      `/v1/invitations/${encodeURIComponent(token)}`,
    );
  acceptInvite = (input: { token: string; displayName: string; password: string; timeZone: string }) =>
    this.post<SessionInfo>('/v1/auth/accept-invite', input);
  acceptInviteSignedIn = (token: string) => this.post<{ ok: true; workspaceId: string }>(`/v1/invitations/${encodeURIComponent(token)}/accept`);
  updateSettings = (input: UpdateSettingsInput) => this.patch<SessionInfo>('/v1/me/settings', input);
  devices = () => this.get<DeviceSession[]>('/v1/me/devices');
  deleteAccount = (password: string) => this.del<{ ok: true }>('/v1/me', { password });
  revokeDevice = (id: string) => this.del<{ ok: true }>(`/v1/me/devices/${id}`);
  registerPush = (platform: 'web' | 'expo' | 'apns' | 'fcm', token: string) => this.post('/v1/me/push', { platform, token });
  unregisterPush = () => this.del('/v1/me/push');
  realtimeTicket = () => this.post<{ ticket: string }>('/v1/realtime/ticket');

  /* ----------------------------------------------------------------- chat */

  conversations = () => this.get<ConversationSummary[]>('/v1/conversations');
  conversation = (id: string) => this.get<ConversationSummary>(`/v1/conversations/${id}`);
  browseChannels = (workspaceId: string) =>
    this.get<{ id: string; name: string; topic: string | null; memberCount: number; isMember: boolean }[]>(`/v1/workspaces/${workspaceId}/channels`);
  createChannel = (input: Partial<CreateChannelInput> & { workspaceId: string; name: string }) =>
    this.post<ConversationSummary>('/v1/conversations/channels', input);
  openDirect = (workspaceId: string, userIds: string[]) => this.post<ConversationSummary>('/v1/conversations/direct', { workspaceId, userIds });
  joinChannel = (id: string) => this.post<ConversationSummary>(`/v1/conversations/${id}/join`);
  leaveConversation = (id: string) => this.post(`/v1/conversations/${id}/leave`);
  conversationMembers = (id: string) => this.get<(PersonRef & { email: string; role: string })[]>(`/v1/conversations/${id}/members`);
  updateMembers = (id: string, addUserIds: string[], removeUserIds: string[]) =>
    this.patch(`/v1/conversations/${id}/members`, { addUserIds, removeUserIds });
  setConversationPrefs = (id: string, notifyLevel: 'all' | 'mentions' | 'mute') => this.patch(`/v1/conversations/${id}/prefs`, { notifyLevel });
  messages = (id: string, opts: { before?: number; limit?: number; thread?: string } = {}) =>
    this.get<MessagePage>(`/v1/conversations/${id}/messages${qs(opts)}`);
  changesSince = (id: string, sinceChange: number) => this.get<MessagePage>(`/v1/conversations/${id}/messages${qs({ sinceChange })}`);
  postMessage = (id: string, input: Partial<PostMessageInput> & { clientKey: string; body: string }) =>
    this.post<ChatMessage>(`/v1/conversations/${id}/messages`, input);
  editMessage = (id: string, body: string, version: number) => this.patch<ChatMessage>(`/v1/messages/${id}`, { body, version });
  deleteMessage = (id: string) => this.del(`/v1/messages/${id}`);
  react = (id: string, emoji: string) => this.put(`/v1/messages/${id}/reactions`, { emoji });
  unreact = (id: string, emoji: string) => this.del(`/v1/messages/${id}/reactions`, { emoji });
  markRead = (id: string, seq: number, threadRootId: string | null = null) => this.post(`/v1/conversations/${id}/read`, { seq, threadRootId });
  shareMailToChat = (input: ShareMailToChatInput) => this.post<ChatMessage>('/v1/mail/share-to-chat', input);

  /* ---------------------------------------------------------------- files */

  uploadFile = (file: Blob, name: string, conversationId?: string | null, guest = false) => {
    const fd = new FormData();
    if (conversationId) fd.append('conversationId', conversationId);
    fd.append('file', file, name);
    return this.post<FileRef>(guest ? '/v1/guest/files' : '/v1/files', fd);
  };
  fileLink = (id: string, guest = false) => this.post<{ url: string; expiresAt: string }>(`/v1/${guest ? 'guest/' : ''}files/${id}/link`);
  files = (opts: { conversationId?: string; q?: string } = {}) => this.get<ListedFile[]>(`/v1/files${qs(opts)}`);

  /* ------------------------------------------------------------- meetings */

  meetings = (filter: 'upcoming' | 'recent' | 'live') => this.get<ListedOccurrence[]>(`/v1/meetings${qs({ filter })}`);
  createMeeting = (input: Partial<CreateMeetingInput> & { title: string; kind: 'instant' | 'scheduled' }) =>
    this.post<{ meeting: MeetingSummary; occurrence: OccurrenceSummary }>('/v1/meetings', input);
  meeting = (id: string) => this.get<MeetingSummary>(`/v1/meetings/${id}`);
  occurrence = (id: string) => this.get<OccurrenceDetail>(`/v1/occurrences/${id}`);
  joinOccurrence = (id: string) => this.post<JoinContext>(`/v1/occurrences/${id}/join`);
  joinByCode = (code: string) => this.post<JoinContext>('/v1/meetings/join-by-code', { code });
  occurrenceStatus = (id: string) => this.get<JoinContext>(`/v1/occurrences/${id}/status`);
  leaveOccurrence = (id: string) => this.post(`/v1/occurrences/${id}/leave`);
  raiseHand = (id: string, raised: boolean) => this.post(`/v1/occurrences/${id}/hand`, { raised });
  decideLobby = (id: string, participantId: string, decision: 'admit' | 'deny') => this.post(`/v1/occurrences/${id}/lobby`, { participantId, decision });
  participantAction = (id: string, participantId: string, action: 'remove' | 'make_cohost' | 'mute') =>
    this.post(`/v1/occurrences/${id}/participants`, { participantId, action });
  controlOccurrence = (id: string, action: 'lock' | 'unlock' | 'end') => this.post(`/v1/occurrences/${id}/control`, { action });

  /* ---------------------------------------------------------------- guest */

  guestPreview = (code: string) =>
    this.get<{ title: string; organizerName: string; guestsAllowed: boolean; state: string; locked: boolean; startsAt: string | null; endsAt: string | null }>(
      `/v1/guest/meetings/${encodeURIComponent(code)}`,
    );
  guestJoin = (code: string, displayName: string) => this.post<JoinContext>(`/v1/guest/meetings/${encodeURIComponent(code)}/join`, { displayName });
  guestStatus = () => this.get<JoinContext>('/v1/guest/status');
  guestParticipants = () => this.get<MeetingParticipant[]>('/v1/guest/participants');
  guestMessages = (sinceChange?: number) => this.get<MessagePage & { conversationId: string }>(`/v1/guest/messages${qs({ sinceChange })}`);
  guestPost = (input: { clientKey: string; body: string; fileIds?: string[] }) => this.post<ChatMessage>('/v1/guest/messages', input);
  guestLeave = () => this.post('/v1/guest/leave');
  guestHand = (raised: boolean) => this.post('/v1/guest/hand', { raised });
  guestTicket = () => this.post<{ ticket: string }>('/v1/guest/realtime/ticket');

  /* ------------------------------------------------------------- accounts */

  accounts = () => this.get<ConnectedAccount[]>('/v1/accounts');
  connectSpacemail = (input: SpacemailConnectInput) => this.post<SpacemailConnectResponse>('/v1/accounts/spacemail', input);
  reconnectSpacemail = (id: string, password: string) => this.post<SpacemailConnectResponse>(`/v1/accounts/${id}/reconnect`, { password });
  /** Suggests how to connect an address. Only a suggestion: the person confirms before anything is sent. */
  detectAccount = (email: string) => this.get<MailDetection>(`/v1/accounts/detect?email=${encodeURIComponent(email)}`);
  /** Any IMAP/SMTP mailbox with a reviewed preset or servers the person entered. */
  connectImap = (input: ImapConnectInput) => this.post<MailboxConnectResponse>('/v1/accounts/imap', input);
  /** New password for a Spacemail or IMAP account; its server settings are kept. */
  reconnectMailbox = (id: string, password: string) => this.post<MailboxConnectResponse>(`/v1/accounts/${id}/reconnect`, { password });
  oauthStart = (provider: 'google' | 'microsoft' | 'zoho', returnTo?: string, accountId?: string) =>
    this.post<{ url: string }>(`/v1/accounts/oauth/${provider}/start`, { returnTo, accountId });
  /** Native apps: one-time link to start provider sign-in in the system browser. */
  oauthHandoff = (provider: 'google' | 'microsoft' | 'zoho', accountId?: string) =>
    this.post<{ url: string; returnUrl: string }>(`/v1/accounts/oauth/${provider}/handoff`, accountId ? { accountId } : {});
  addSampleMailbox = () => this.post<ConnectedAccount>('/v1/accounts/sample');
  updateAccount = (id: string, input: { displayName?: string; label?: string; color?: string; includeInUnified?: boolean }) => this.patch<ConnectedAccount>(`/v1/accounts/${id}`, input);
  reorderAccounts = (ids: string[]) => this.put<ConnectedAccount[]>('/v1/accounts/order', { ids });
  syncAccount = (id: string) => this.post(`/v1/accounts/${id}/sync`);
  disconnectAccount = (id: string) => this.del(`/v1/accounts/${id}`);

  /* ----------------------------------------------------------------- mail */

  mailFolders = () => this.get<MailFolder[]>('/v1/mail/folders');
  mailMessages = (q: MailQuery) => this.get<MailPage>(`/v1/mail/messages${qs({ ...q })}`);
  mailMessage = (id: string, allowImages = false) => this.get<MailDetail>(`/v1/mail/messages/${id}${allowImages ? '?images=allow' : ''}`);
  mailThread = (id: string) => this.get<MailSummary[]>(`/v1/mail/messages/${id}/thread`);
  attachmentUrl = (messageId: string, attachmentId: string) => `${this.baseUrl}/v1/mail/messages/${messageId}/attachments/${encodeURIComponent(attachmentId)}`;
  mailAction = (input: { operationId: string; messageIds: string[]; action: 'archive' | 'trash' | 'markRead' | 'markUnread' | 'star' | 'unstar' | 'move'; targetFolderId?: string }) =>
    this.post<Operation[]>('/v1/mail/actions', input);
  drafts = () => this.get<Draft[]>('/v1/mail/drafts');
  draft = (id: string) => this.get<Draft>(`/v1/mail/drafts/${id}`);
  createDraft = (input: Partial<DraftUpsertInput> & { accountId: string }) => this.post<Draft>('/v1/mail/drafts', input);
  saveDraft = (id: string, input: Partial<DraftUpsertInput> & { accountId: string }) => this.put<Draft>(`/v1/mail/drafts/${id}`, input);
  deleteDraft = (id: string) => this.del(`/v1/mail/drafts/${id}`);
  sendMail = (input: { operationId: string; draftId: string; accountId: string }) => this.post<Operation>('/v1/mail/send', input);
  operations = () => this.get<Operation[]>('/v1/operations');
  reconcileOperation = (id: string) => this.post<{ state: string; detail: string }>(`/v1/operations/${id}/reconcile`);
  dismissOperation = (id: string) => this.post<{ ok: true; draftId: string | null }>(`/v1/operations/${id}/dismiss`);

  /* ------------------------------------------------------------- calendar */

  calendars = () => this.get<CalendarInfo[]>('/v1/calendars');
  updateCalendar = (id: string, input: { syncEnabled?: boolean; color?: string }) => this.patch(`/v1/calendars/${id}`, input);
  events = (from: Date, to: Date, calendarIds?: string[]) =>
    this.get<CalendarEvent[]>(`/v1/calendar/events${qs({ from: from.toISOString(), to: to.toISOString(), calendarIds: calendarIds?.join(',') })}`);
  createEvent = (input: Partial<EventInput> & Pick<EventInput, 'operationId' | 'calendarId' | 'title' | 'start' | 'end' | 'timeZone'>) =>
    this.post<{ eventId: string; operation?: Operation; meetingId: string | null }>('/v1/calendar/events', input);
  updateEvent = (id: string, input: Partial<EventUpdateInput> & { operationId: string }) => this.patch(`/v1/calendar/events/${encodeURIComponent(id)}`, input);
  cancelEvent = (id: string, operationId: string, scope: 'occurrence' | 'series' = 'series') =>
    this.del(`/v1/calendar/events/${encodeURIComponent(id)}${qs({ operationId, scope })}`);
  rsvp = (id: string, operationId: string, status: 'accepted' | 'declined' | 'tentative') =>
    this.post(`/v1/calendar/events/${encodeURIComponent(id)}/rsvp`, { operationId, status });

  /* ------------------------------------------------------- shared modules */

  search = (q: string, scope: 'all' | 'mail' | 'events' | 'chat' | 'people' | 'files' = 'all', signal?: AbortSignal) =>
    this.get<SearchResults>(`/v1/search${qs({ q, scope })}`, signal);
  people = (q?: string) => this.get<Person[]>(`/v1/people${qs({ q })}`);
  person = (id: string) => this.get<PersonCard>(`/v1/people/${id}`);
  activity = (unread = false) => this.get<ActivityItem[]>(`/v1/activity${qs({ unread: unread ? '1' : undefined })}`);
  activityCount = () => this.get<{ unread: number }>('/v1/activity/count');
  markActivityRead = (input: { ids?: string[]; all?: boolean }) => this.post('/v1/activity/read', input);
  workspaceMembers = (id: string) => this.get<WorkspaceMember[]>(`/v1/workspaces/${id}/members`);
  invitations = (id: string) => this.get<Invitation[]>(`/v1/workspaces/${id}/invitations`);
  invite = (id: string, email: string, role: 'admin' | 'member') => this.post<Invitation>(`/v1/workspaces/${id}/invitations`, { email, role });
  revokeInvitation = (id: string, invitationId: string) => this.del(`/v1/workspaces/${id}/invitations/${invitationId}`);
  removeMember = (id: string, userId: string) => this.del(`/v1/workspaces/${id}/members/${userId}`);

  /* --------------------------------------------------------------- remote */

  remoteHosts = () => this.get<RemoteHost[]>('/v1/remote/hosts');
  remoteHost = (id: string) => this.get<RemoteHost & { access: RemoteHostAccess[] }>(`/v1/remote/hosts/${id}`);
  updateRemoteHost = (id: string, input: { name?: string; allowUnattended?: boolean }) => this.patch<RemoteHost>(`/v1/remote/hosts/${id}`, input);
  removeRemoteHost = (id: string) => this.del<{ ok: true }>(`/v1/remote/hosts/${id}`);
  setRemoteAccess = (hostId: string, userId: string, role: 'control' | 'view') => this.put(`/v1/remote/hosts/${hostId}/access/${userId}`, { role });
  removeRemoteAccess = (hostId: string, userId: string) => this.del(`/v1/remote/hosts/${hostId}/access/${userId}`);
  connectRemoteHost = (
    hostId: string,
    input: { permissions?: RemotePermission[]; grant?: SignedEnvelope | null; cert?: SignedEnvelope | null; conversationId?: string | null } = {},
  ) => this.post<RemoteSession>(`/v1/remote/hosts/${hostId}/sessions`, input);
  createSupportCode = (workspaceId: string, conversationId?: string | null) =>
    this.post<RemoteSupportCode>('/v1/remote/support-codes', { workspaceId, conversationId: conversationId ?? null });
  supportCode = (id: string) => this.get<RemoteSupportCode>(`/v1/remote/support-codes/${id}`);
  cancelSupportCode = (id: string) => this.del<{ ok: true }>(`/v1/remote/support-codes/${id}`);
  remoteSessions = (active = false) => this.get<RemoteSession[]>(`/v1/remote/sessions${qs({ active: active ? '1' : undefined })}`);
  remoteSession = (id: string) => this.get<RemoteSession>(`/v1/remote/sessions/${id}`);
  remoteTicket = (id: string) => this.post<RemoteTicket>(`/v1/remote/sessions/${id}/ticket`);
  endRemoteSession = (id: string, reason?: 'answer_not_verified' | 'viewer_error') =>
    this.post<{ ok: true }>(`/v1/remote/sessions/${id}/end`, reason ? { reason } : {});
  remoteEnrollments = (workspaceId: string) => this.get<RemoteEnrollment[]>(`/v1/remote/enrollments${qs({ workspaceId })}`);
  createEnrollment = (input: { workspaceId: string; scope: 'personal' | 'workspace'; allowUnattended: boolean; maxUses?: number; expiresInHours?: number }) =>
    this.post<RemoteEnrollment>('/v1/remote/enrollments', input);
  revokeEnrollment = (id: string) => this.del<{ ok: true }>(`/v1/remote/enrollments/${id}`);
  remoteLink = (userCode: string) => this.get<RemoteLinkPreview>(`/v1/remote/link/${encodeURIComponent(userCode)}`);
  approveRemoteLink = (
    userCode: string,
    input: { workspaceId: string; scope: 'personal' | 'workspace'; allowUnattended: boolean; approval?: SignedEnvelope | null },
  ) => this.post<{ hostId: string }>(`/v1/remote/link/${encodeURIComponent(userCode)}/approve`, input);
  denyRemoteLink = (userCode: string) => this.post<{ ok: true }>(`/v1/remote/link/${encodeURIComponent(userCode)}/deny`);
  remoteKeys = () => this.get<RemoteUserKey[]>('/v1/remote/keys');
  registerRemoteKey = (publicKey: string, label: string) => this.post<RemoteUserKey>('/v1/remote/keys', { publicKey, label });
  revokeRemoteKey = (id: string) => this.del<{ ok: true }>(`/v1/remote/keys/${id}`);
  remoteAuthority = (workspaceId: string) => this.get<RemoteAuthority>(`/v1/remote/workspaces/${workspaceId}/authority`);
  putRemoteAuthority = (workspaceId: string, envelope: SignedEnvelope) => this.put<RemoteAuthority>(`/v1/remote/workspaces/${workspaceId}/authority`, { envelope });
  pendingRemoteKeys = (workspaceId: string) => this.get<RemoteUserKey[]>(`/v1/remote/workspaces/${workspaceId}/keys`);
  putRemoteCert = (workspaceId: string, keyId: string, cert: SignedEnvelope) => this.put(`/v1/remote/workspaces/${workspaceId}/keys/${keyId}/cert`, { cert });
  resetRemoteAuthority = (workspaceId: string, envelope: SignedEnvelope) =>
    this.post<RemoteAuthority>(`/v1/remote/workspaces/${workspaceId}/authority/reset`, { envelope });
  workspaceDomains = (workspaceId: string) => this.get<WorkspaceDomain[]>(`/v1/remote/workspaces/${workspaceId}/domains`);
  addWorkspaceDomain = (workspaceId: string, domain: string) => this.post<WorkspaceDomain>(`/v1/remote/workspaces/${workspaceId}/domains`, { domain });
  verifyWorkspaceDomain = (workspaceId: string, domain: string) =>
    this.post<WorkspaceDomain>(`/v1/remote/workspaces/${workspaceId}/domains/${encodeURIComponent(domain)}/verify`);
  removeWorkspaceDomain = (workspaceId: string, domain: string) => this.del<{ ok: true }>(`/v1/remote/workspaces/${workspaceId}/domains/${encodeURIComponent(domain)}`);
}
